'use client';

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type React from 'react';

import { AxisBottom, AxisRight, type TickRendererProps } from '@visx/axis';
import { curveLinear } from '@visx/curve';
import { localPoint } from '@visx/event';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Area, LinePath } from '@visx/shape';

import useHorizontalTouchGuard from 'components/Graph/components/useHorizontalTouchGuard';
import { topBufferSize } from 'components/Graph/components/utils';
import Axis from 'DesignSystem2/Graph/Axis';
import DateLine, { DATE_LINE_LABEL_CLEARANCE } from 'DesignSystem2/Graph/LineGraph/DateLine';
import { xTickFormatFromRange } from 'DesignSystem2/Graph/utils';
import Palette from 'DesignSystem2/Theme/Palette';
import { Body } from 'DesignSystem3/Text/Body';
import { Emphasis } from 'DesignSystem3/Text/Emphasis';
import type { KpowHistoryPoint } from 'react_pages/AmericanPowerIndex/api/kpowApi';

import type { KpowAnnotation } from '../kpowAnnotations';
import { KPOW_BLUE, KPOW_PURPLE, KPOW_RED, midtermsPartyColor, signedColor } from '../kpowColors';
import { DAY_MS, formatLongDate, HOUR_MS, KPOW_AXIS_MAX, type KpowPeriod, pointDate } from '../kpowFormat';

type IndexGraphPresentation = 'kpow' | 'midterms';

interface IndexGraphContentProps {
    width: number;
    height: number;
    points: KpowHistoryPoint[];
    annotations?: KpowAnnotation[];
    onHoverPoint?: (point: KpowHistoryPoint | null) => void;
    /** Selected time range; drives x-axis tick granularity. Defaults to the full range. */
    period?: KpowPeriod;
    presentation?: IndexGraphPresentation;
}

// Edge margin for x-axis label spill only (edgeSafeAnchor, DateLine clamp). The plot line itself starts
// flush at x=0 so it begins where the full-width frame bars start.
const HORIZONTAL_PADDING = 12;
// Right margin reserved for the whole y-axis unit (tick gap + labels + vertical strip). KPOW retains
// its original 28px gap; the Midterms treatment uses the tighter 12px spacing from the Elections Hub.
const Y_TICK_LENGTH = 28;
const MIDTERMS_Y_TICK_LENGTH = 12;
const Y_AXIS_LABEL_WIDTH = 40;
const MIDTERMS_COMPACT_Y_AXIS_LABEL_WIDTH = 40;
const MIDTERMS_Y_AXIS_LABEL_WIDTH = 64;
const MIDTERMS_COMPACT_AXIS_MAX_WIDTH = 599;
const Y_AXIS_STRIP_WIDTH = 3;
// Below the plot, in order: a gap clearing the bottom y-axis label + matching the strip's top inset,
// the bottom frame bar, then the month labels — all fit within VERTICAL_AXIS_SIZE. The bar sits 20px
// below the strip's bottom overhang (Y_STRIP_OVERHANG) so the gap mirrors the strip's 20px gap to the
// tab-row divider above (gap-2 16px + topBufferSize 16px - overhang 12px).
const AXIS_BAR_OFFSET = 32; // plot bottom -> bottom frame bar
const AXIS_LABEL_OFFSET = 40; // plot bottom -> month labels (sit just below the bar)
const MIDTERMS_AXIS_LABEL_OFFSET = 4; // extra breathing room below the bottom (+D max) gridline
const VERTICAL_AXIS_SIZE = 66;
// Midterms has no bottom frame bar and uses short month labels, so it only needs
// enough room for the 4px axis gap and the label line. Let the plot consume the
// rest of its fixed 350px container instead of leaving the KPOW-sized reserve.
const MIDTERMS_VERTICAL_AXIS_SIZE = 24;
// Extend the right-edge gradient strip past the plot top/bottom so the top/bottom y labels sit within
// it. Asymmetric: the plot sits ~36px below the tab divider (gap-2 16px + a doubled topBufferSize from
// the yScale range start + the Group translate) but only AXIS_BAR_OFFSET-BOTTOM below the bottom bar.
// A taller top overhang lifts the strip's top edge so both ends clear their black bar by the same 20px.
const Y_STRIP_OVERHANG_TOP = 28;
const Y_STRIP_OVERHANG_BOTTOM = 12;
// The tab-row divider (the "top" frame bar) lives in ChartCard, one card gap-2 (16px) above the SVG's
// top edge. Annotation guide lines extend up past the SVG top (overflow:visible) to meet it, bridging
// the top and bottom frame bars. In Group-local coords the divider is at -(topBufferSize + this).
const TAB_DIVIDER_GAP = 16;

/** Center an x-axis label on its tick, falling back to start/end anchoring only when it would spill off the edge. */
const edgeSafeAnchor = (tickX: number, contentWidth: number): 'start' | 'middle' | 'end' => {
    const halfLabel = 30; // ~half of a "Mon YYYY" label at 12px
    if (tickX - halfLabel < HORIZONTAL_PADDING) return 'start';
    if (tickX + halfLabel > contentWidth) return 'end';
    return 'middle';
};

/** Pixel window around the cursor within which scrubbing activates an annotation's tooltip. */
const ANNOTATION_ACTIVATE_PX = 18;

/** Figma RichTooltip dimensions and chart-edge inset for Midterms checkpoints. */
const CHECKPOINT_TOOLTIP_WIDTH = 250;
const CHECKPOINT_TOOLTIP_HEIGHT = 90;
const CHECKPOINT_TOOLTIP_GAP = 10;
const CHECKPOINT_TOOLTIP_INSET = 8;

/** Vertical overscan for the full-height clip rects so they cover the plot plus its top buffer / halos. */
const CLIP_OVERSCAN = 2000;

const Y_TICK_FORMAT = (n: number): string => {
    if (Math.abs(n) < 0.05) return '0';
    // Adaptive timeframes can produce sub-integer ticks; show one decimal unless the value is whole.
    const abs = Math.abs(n);
    const label = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
    return n > 0 ? `${label} R` : `${label} D`;
};

const MIDTERMS_Y_TICK_FORMAT = (n: number, compact = false): string => {
    if (Math.abs(n) < 0.05) return 'Tie';
    const value = `+${Math.abs(n)}`;
    return compact ? value : `${value} ${n > 0 ? 'R' : 'D'}`;
};

/** Render full party labels when space allows; compact formatting omits the suffix upstream. */
const MidtermsYAxisTick = ({
    formattedValue,
    verticalAnchor: _verticalAnchor,
    ...textProps
}: TickRendererProps): JSX.Element | null => {
    if (!formattedValue) return null;
    const partyMatch = formattedValue.match(/^(.*) ([RD])$/);

    return (
        <text {...textProps} textAnchor="start" dominantBaseline="middle" aria-label={formattedValue}>
            {partyMatch?.[1] ?? formattedValue}
            {partyMatch ? <tspan data-axis-party-suffix={partyMatch[2]}>{` ${partyMatch[2]}`}</tspan> : null}
        </text>
    );
};

/** Day-precision date for the Midterms scrub label; module-level so the prop identity stays stable. */
const MIDTERMS_SCRUB_DATE_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

// Saturated red/blue for the right-edge axis labels (R above 0, D below), kept legible at 12px by
// the white-stroked halo. The neutral "0" tick is purple to read as the midpoint between parties.
const yTickColor = (n: number): string => {
    if (n > 0.5) return KPOW_RED;
    if (n < -0.5) return KPOW_BLUE;
    return KPOW_PURPLE;
};

interface YAxisConfig {
    domainMin: number;
    domainMax: number;
    ticks: number[];
    /** Actual data extremes in the window; the fill gradient anchors to these so it stays strong at the line. */
    dataLo: number;
    dataHi: number;
}

/**
 * Symmetric Y domain: round the window's data extreme up to the next even integer for the top/bottom
 * label (peak 7 -> 8, floor 2), with the mid label at half of that (8 -> 4). Yields a clean symmetric
 * max / mid / 0 / mid / max axis that always keeps 0 in view for the binary red/blue flip.
 */
const computeYAxis = (points: KpowHistoryPoint[]): YAxisConfig => {
    const values = points.map((p) => p.signed_value);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const dataExtreme = Math.max(Math.abs(lo), Math.abs(hi));
    const maxLabel = Math.min(KPOW_AXIS_MAX, Math.max(2, Math.ceil(dataExtreme / 2) * 2));
    const mid = maxLabel / 2;
    return {
        domainMin: -maxLabel,
        domainMax: maxLabel,
        ticks: [-maxLabel, -mid, 0, mid, maxLabel],
        dataLo: lo,
        dataHi: hi,
    };
};

// Tighter baseline window than KPOW's adaptive axis so day-to-day deltas read at a glance. When the
// data outgrows it, the window expands to the next whole number so the line always stays in frame.
const MIDTERMS_AXIS_BASE_MAX = 7;

const computeMidtermsYAxis = (points: KpowHistoryPoint[]): YAxisConfig => {
    const values = points.map((point) => point.signed_value);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const maxLabel = Math.max(MIDTERMS_AXIS_BASE_MAX, Math.ceil(Math.max(Math.abs(lo), Math.abs(hi))));
    const mid = maxLabel / 2;
    return {
        domainMin: -maxLabel,
        domainMax: maxLabel,
        ticks: [-maxLabel, -mid, 0, mid, maxLabel],
        dataLo: lo,
        dataHi: hi,
    };
};

// KPOW keeps the high-frequency shape of ~4000 hourly points. The Midterms hub is a smaller overview,
// so it uses fewer averaging buckets for a calmer line while hover/checkpoint lookups retain full data.
const KPOW_LINE_TARGET_POINTS = 2000;
const MIDTERMS_LINE_TARGET_POINTS = 600;

/** One tick per month-start within [start, end], so the axis labels read Nov, Dec, Jan, ... without gaps. */
const monthStartTicks = (start: Date, end: Date): Date[] => {
    const ticks: Date[] = [];
    const year = start.getFullYear();
    let month = start.getMonth();
    // Skip the partial leading month so the first tick is a clean month-start
    // at or after `start`.
    if (new Date(year, month, 1).getTime() < start.getTime()) {
        month += 1;
    }
    while (true) {
        const cursor = new Date(year + Math.floor(month / 12), month % 12, 1);
        if (cursor.getTime() > end.getTime()) break;
        ticks.push(cursor);
        month += 1;
    }
    return ticks;
};

/** Local midnight of `d`. */
const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** One tick per midnight within [start, end] — used for the 1W view. */
const dayStartTicks = (start: Date, end: Date): Date[] => {
    const ticks: Date[] = [];
    let cursor = startOfDay(start);
    if (cursor.getTime() < start.getTime()) cursor = new Date(cursor.getTime() + DAY_MS);
    while (cursor.getTime() <= end.getTime()) {
        ticks.push(cursor);
        cursor = new Date(cursor.getTime() + DAY_MS);
    }
    return ticks;
};

/** Ticks every `stepHours`, aligned to the day boundary, within [start, end] — used for the 1D view. */
const hourStepTicks = (start: Date, end: Date, stepHours: number): Date[] => {
    const ticks: Date[] = [];
    const step = stepHours * HOUR_MS;
    let t = startOfDay(start).getTime();
    while (t < start.getTime()) t += step;
    while (t <= end.getTime()) {
        ticks.push(new Date(t));
        t += step;
    }
    return ticks;
};

/** Pick x-axis tick positions + label format for the selected time range. */
const computeXAxis = (
    period: KpowPeriod,
    start: Date,
    end: Date,
    primaryContentWidth: number,
): { xTicks: Date[]; xAxisTickFormat: Intl.DateTimeFormatOptions } => {
    if (period === '1D') {
        return { xTicks: hourStepTicks(start, end, 6), xAxisTickFormat: { hour: 'numeric' } };
    }
    if (period === '1W') {
        return { xTicks: dayStartTicks(start, end), xAxisTickFormat: { month: 'short', day: 'numeric' } };
    }
    if (period === '1M') {
        // ~6 ticks across a month (every 5 days) so day labels don't crowd.
        return { xTicks: hourStepTicks(start, end, 24 * 5), xAxisTickFormat: { month: 'short', day: 'numeric' } };
    }
    // 'All' / YTD / QTD: month-start ticks. Include the year on desktop so a range crossing a year
    // boundary reads unambiguously; drop it on narrow viewports so every month fits.
    const xAxisTickFormat: Intl.DateTimeFormatOptions =
        primaryContentWidth < 500 ? { month: 'short' } : { month: 'short', year: 'numeric' };
    return { xTicks: monthStartTicks(start, end), xAxisTickFormat };
};

/**
 * Bucket the series into `target` evenly-spaced buckets and average the
 * signed_value within each bucket. Cheap, deterministic, and visibly smoother
 * than raw hourly data over a 6-month window. The full `points` array stays
 * available for tooltip/annotation lookups so hover precision isn't lost.
 */
const downsampleForLine = (points: KpowHistoryPoint[], target: number): KpowHistoryPoint[] => {
    if (points.length <= target) return points;
    const bucketSize = points.length / target;
    const result: KpowHistoryPoint[] = [];
    for (let i = 0; i < target; i += 1) {
        const start = Math.floor(i * bucketSize);
        const end = Math.floor((i + 1) * bucketSize);
        const bucket = points.slice(start, end);
        if (bucket.length === 0) continue;
        const avg = bucket.reduce((s, p) => s + p.signed_value, 0) / bucket.length;
        const mid = bucket[Math.floor(bucket.length / 2)];
        // Display fields are only consumed by hover/scrub, which uses the
        // original `points`. The downsampled output is line-only.
        result.push({ ...mid, signed_value: avg });
    }
    // Preserve the latest input point verbatim so the line's right edge lands on
    // the exact snapshot value rather than a bucket average that blends the
    // snapshot with the last few hourly points (which can read ~0.1 too low).
    const latest = points[points.length - 1];
    if (result.length === 0 || result[result.length - 1].end_period_ts !== latest.end_period_ts) {
        result.push(latest);
    } else {
        result[result.length - 1] = latest;
    }
    return result;
};

/** signed_value of the history point closest to a Unix-second timestamp. */
const valueAtTime = (points: KpowHistoryPoint[], unixSeconds: number): number => {
    const target = unixSeconds * 1000;
    let best = points[0];
    let bestDelta = Math.abs(pointDate(best.end_period_ts).getTime() - target);
    for (let i = 1; i < points.length; i += 1) {
        const delta = Math.abs(pointDate(points[i].end_period_ts).getTime() - target);
        if (delta < bestDelta) {
            best = points[i];
            bestDelta = delta;
        }
    }
    return best.signed_value;
};

const IndexGraphContent = ({
    width,
    height,
    points,
    annotations = [],
    onHoverPoint,
    period = 'All',
    presentation = 'kpow',
}: IndexGraphContentProps): JSX.Element | null => {
    const isMidterms = presentation === 'midterms';
    const compactMidtermsAxis = isMidterms && width <= MIDTERMS_COMPACT_AXIS_MAX_WIDTH;
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [activeAnnotation, setActiveAnnotation] = useState<number | null>(null);
    // Touch handlers reference the latest tooltip/leave handlers via a ref so the
    // hook itself can be called before the early-return guards (rules-of-hooks),
    // while the actual handler logic is defined further down with full access to
    // the chart scales.
    const tooltipRef = useRef<{ tooltip: (e: React.TouchEvent<SVGElement>) => void; leave: () => void } | null>(null);
    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useHorizontalTouchGuard<SVGElement>({
        onTouchStart: (e) => tooltipRef.current?.tooltip(e),
        onHorizontalMove: (e) => tooltipRef.current?.tooltip(e),
        onTouchEnd: () => tooltipRef.current?.leave(),
    });
    const yTickLength = isMidterms ? MIDTERMS_Y_TICK_LENGTH : Y_TICK_LENGTH;
    const yAxisLabelWidth = compactMidtermsAxis
        ? MIDTERMS_COMPACT_Y_AXIS_LABEL_WIDTH
        : isMidterms
          ? MIDTERMS_Y_AXIS_LABEL_WIDTH
          : Y_AXIS_LABEL_WIDTH;
    const rightLabelPadding = yTickLength + yAxisLabelWidth + Y_AXIS_STRIP_WIDTH;
    const primaryContentWidth = width - rightLabelPadding;
    const verticalAxisSize = isMidterms ? MIDTERMS_VERTICAL_AXIS_SIZE : VERTICAL_AXIS_SIZE;
    const primaryContentHeight = height - verticalAxisSize - topBufferSize;

    // Derived data is memoized so scrub re-renders (every mousemove) don't rebuild the full ~4000-point
    // times array, re-downsample the rendered line, or recompute the y-domain. `points` is a stable
    // reference between frames (windowed + memoized upstream), so these only recompute on data/period/size
    // changes. They run before the early return, so they stay unconditional (rules of hooks).
    const times = useMemo(() => points.map((p) => pointDate(p.end_period_ts)), [points]);
    const yAxis = useMemo(
        () => (isMidterms ? computeMidtermsYAxis(points) : computeYAxis(points)),
        [isMidterms, points],
    );
    // Downsampled series for the rendered line only; hover/scrub/annotations still index full `points`.
    const lineTargetPoints = isMidterms ? MIDTERMS_LINE_TARGET_POINTS : KPOW_LINE_TARGET_POINTS;
    const linePoints = useMemo(() => downsampleForLine(points, lineTargetPoints), [lineTargetPoints, points]);
    const xScale = useMemo(
        () =>
            scaleTime<number>({
                domain: [times[0], times[times.length - 1]],
                range: [0, primaryContentWidth],
            }),
        [times, primaryContentWidth],
    );
    const yScale = useMemo(
        () =>
            scaleLinear<number>({
                domain: [yAxis.domainMax, yAxis.domainMin],
                range: [topBufferSize, primaryContentHeight],
            }),
        [yAxis, primaryContentHeight],
    );
    // Annotation dots in the visible range, positioned on the rendered curve. Invariant during scrub,
    // so memoized; the scrub handler reads it to activate the nearest one and grey out passed dots.
    const annotationMarks = useMemo(() => {
        if (linePoints.length === 0) return [];
        const firstMs = times[0].getTime();
        const lastMs = times[times.length - 1].getTime();
        return annotations
            .filter((a) => {
                const ms = a.ts * 1000;
                return ms >= firstMs && ms <= lastMs;
            })
            .map((annotation) => ({
                annotation,
                cx: xScale(pointDate(annotation.ts)) ?? 0,
                // y on the downsampled series (which the line is drawn from) so dots land precisely on
                // the rendered curve, not a few px off from bucket averaging.
                cy: yScale(valueAtTime(linePoints, annotation.ts)) ?? 0,
            }));
    }, [annotations, linePoints, times, xScale, yScale]);

    const handleTooltip = (event: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>): void => {
        const { x } = localPoint(event) || { x: 0 };
        if (x <= 0 || x > primaryContentWidth) {
            setActiveIndex(null);
            setActiveAnnotation(null);
            onHoverPoint?.(null);
            return;
        }
        const targetTime = xScale.invert(x).getTime();
        let best = 0;
        let bestDelta = Math.abs(times[0].getTime() - targetTime);
        for (let i = 1; i < times.length; i += 1) {
            const delta = Math.abs(times[i].getTime() - targetTime);
            if (delta < bestDelta) {
                best = i;
                bestDelta = delta;
            }
        }
        setActiveIndex(best);
        // Midterms treats a cursor parked on the newest reading as idle (the `scrubPoint`
        // derivation below keeps the chart's trailing dot for the same reason), so consumers
        // hear null and keep their LIVE headline — the rule has exactly one owner.
        onHoverPoint?.(isMidterms && best === points.length - 1 ? null : points[best]);
        // Activate the nearest annotation within a small pixel window so scrubbing (not just hovering
        // the dot itself) opens its tooltip.
        let nearestAnnotation: number | null = null;
        let nearestDist = ANNOTATION_ACTIVATE_PX;
        annotationMarks.forEach((mark, i) => {
            const dist = Math.abs(mark.cx - x);
            if (dist <= nearestDist) {
                nearestDist = dist;
                nearestAnnotation = i;
            }
        });
        setActiveAnnotation(nearestAnnotation);
    };

    const handleLeave = (): void => {
        setActiveIndex(null);
        setActiveAnnotation(null);
        onHoverPoint?.(null);
    };

    // Wire the touch-guard's stable callbacks to the freshly-built handlers. Assigned in an
    // every-render effect rather than during render: a render-phase ref write disqualifies
    // the whole component from React Compiler memoization, which is what keeps per-mousemove
    // scrub renders from rebuilding the line/axis subtrees.
    useEffect(() => {
        tooltipRef.current = { tooltip: handleTooltip, leave: handleLeave };
    });

    if (points.length === 0 || width <= 0 || primaryContentWidth <= 0 || primaryContentHeight <= 0) {
        return null;
    }

    const { domainMin, domainMax, ticks, dataLo, dataHi } = yAxis;
    const gradientId = `kpow-balance-gradient-${width}-${height}`;
    const fillGradientId = `kpow-balance-fill-${width}-${height}`;
    const checkpointShadowId = `${gradientId}-checkpoint-shadow`;
    // Per-period id so the draw-in wipe replays on load and whenever the timeframe changes.
    const revealClipId = `kpow-reveal-${period}`;

    // Tick granularity follows the selected period: hours (1D), days (1W), or month-starts (All).
    const range: [Date, Date] = [times[0], times[times.length - 1]];
    const { xTicks, xAxisTickFormat: computedXAxisTickFormat } = computeXAxis(
        period,
        times[0],
        times[times.length - 1],
        primaryContentWidth,
    );
    const xAxisTickFormat: Intl.DateTimeFormatOptions = isMidterms ? { month: 'short' } : computedXAxisTickFormat;
    const xTickFormat = xTickFormatFromRange(range);

    const xAccessor = (p: KpowHistoryPoint): number => xScale(pointDate(p.end_period_ts)) ?? 0;
    const yAccessor = (p: KpowHistoryPoint): number => yScale(p.signed_value) ?? 0;
    // One line definition, reused across the scrub-dim split (before/after cursor) and the idle case.
    const line = (
        <LinePath<KpowHistoryPoint>
            data={linePoints}
            x={xAccessor}
            y={yAccessor}
            stroke={`url(#${gradientId})`}
            strokeWidth={1.5}
            curve={curveLinear}
            fill="none"
        />
    );
    const activePoint = activeIndex !== null ? points[activeIndex] : null;
    const activeX = activePoint ? xAccessor(activePoint) : 0;
    const activeY = activePoint ? yAccessor(activePoint) : 0;
    // End-of-line "live" indicator: pulsing halo + solid dot at the latest point. Midterms uses the
    // discrete current-leader color; KPOW retains its interpolated threshold color.
    const lastPoint = points[points.length - 1];
    // Midterms reads a cursor parked on the newest point as idle — the hub headline keeps its LIVE
    // pill there, so the chart likewise keeps its solid trailing dot instead of dimming the (empty)
    // future and swapping in a scrub ring. KPOW's existing edge behavior is unchanged.
    const scrubPoint = activePoint && (!isMidterms || activePoint !== lastPoint) ? activePoint : null;
    const lastX = lastPoint ? xAccessor(lastPoint) : 0;
    const lastY = lastPoint ? yAccessor(lastPoint) : 0;
    const lastColor = lastPoint
        ? isMidterms
            ? midtermsPartyColor(lastPoint.signed_value)
            : signedColor(lastPoint.signed_value)
        : 'transparent';

    const activeMark = activeAnnotation !== null ? annotationMarks[activeAnnotation] : null;
    const checkpointTooltipWidth = Math.min(CHECKPOINT_TOOLTIP_WIDTH, width - CHECKPOINT_TOOLTIP_INSET * 2);
    const checkpointTooltipHalfWidth = checkpointTooltipWidth / 2;
    const checkpointTooltipMinX = checkpointTooltipHalfWidth + CHECKPOINT_TOOLTIP_INSET;
    const checkpointTooltipMaxX = width - checkpointTooltipHalfWidth - CHECKPOINT_TOOLTIP_INSET;
    const checkpointTooltipX = activeMark
        ? checkpointTooltipMinX <= checkpointTooltipMaxX
            ? Math.max(checkpointTooltipMinX, Math.min(checkpointTooltipMaxX, activeMark.cx))
            : width / 2
        : 0;
    const checkpointTooltipY = activeMark ? topBufferSize + activeMark.cy : 0;
    const checkpointTooltipAbove = checkpointTooltipY >= CHECKPOINT_TOOLTIP_HEIGHT + CHECKPOINT_TOOLTIP_GAP;

    return (
        <div className="relative" style={{ width, height }}>
            <svg
                width={width}
                height={height}
                style={{ touchAction: 'pan-y', overflow: 'visible' }}
                onMouseMove={handleTooltip}
                onMouseLeave={handleLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <defs>
                    {/* Draw-in wipe: a full-height rect whose width animates 0 -> full on mount, so the
                        chart reveals left-to-right when data loads (and on timeframe change, via the
                        period-keyed id). fill defaults to remove, so it reverts to the base width and
                        stays correct on resize. */}
                    <clipPath id={revealClipId}>
                        {/* Left-to-right draw-in via the shared CSS wipe (transform: scaleX(0->1) from the
                            left). key={period} remounts the rect so the animation replays on load and on
                            timeframe change. */}
                        <rect
                            key={period}
                            x={0}
                            y={-CLIP_OVERSCAN}
                            width={width}
                            height={CLIP_OVERSCAN * 2}
                            className="chart-reveal-wipe"
                        />
                    </clipPath>
                    {/* Binary line color: solid red above the 0 line, a hard flip to solid blue below
                        it (two stops at offset 0.5, the symmetric domain's midpoint = value 0). */}
                    <linearGradient
                        id={gradientId}
                        x1="0"
                        y1={yScale(domainMax)}
                        x2="0"
                        y2={yScale(domainMin)}
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor={KPOW_RED} />
                        <stop offset="0.5" stopColor={KPOW_RED} />
                        <stop offset="0.5" stopColor={KPOW_BLUE} />
                        <stop offset="1" stopColor={KPOW_BLUE} />
                    </linearGradient>
                    {/* Diverging area fill. Anchored at the data extremes (not the wide domain) so the
                        tint stays strong right at the line and fades to transparent exactly at the 0
                        line: a red wash above 0, a blue wash below. Split by 0-line clip so each side
                        only paints its own half. */}
                    <linearGradient
                        id={`${fillGradientId}-r`}
                        x1="0"
                        y1={yScale(dataHi > 0 ? dataHi : 0.001)}
                        x2="0"
                        y2={yScale(0)}
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor={KPOW_RED} stopOpacity={0.4} />
                        <stop offset="1" stopColor={KPOW_RED} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                        id={`${fillGradientId}-b`}
                        x1="0"
                        y1={yScale(0)}
                        x2="0"
                        y2={yScale(dataLo < 0 ? dataLo : -0.001)}
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor={KPOW_BLUE} stopOpacity={0} />
                        <stop offset="1" stopColor={KPOW_BLUE} stopOpacity={0.4} />
                    </linearGradient>
                    <clipPath id={`${fillGradientId}-above`}>
                        <rect x={0} y={-CLIP_OVERSCAN} width={primaryContentWidth} height={yScale(0) + CLIP_OVERSCAN} />
                    </clipPath>
                    <clipPath id={`${fillGradientId}-below`}>
                        <rect x={0} y={yScale(0)} width={primaryContentWidth} height={CLIP_OVERSCAN} />
                    </clipPath>
                    {isMidterms ? (
                        <>
                            {/* Figma Shadow/5 on the 12px checkpoint: a soft offset shadow plus a
                                tighter ambient shadow. The bounds preserve its full 28px footprint. */}
                            <filter id={checkpointShadowId} x="-66.67%" y="-33.33%" width="233.34%" height="233.33%">
                                <feDropShadow
                                    in="SourceGraphic"
                                    dx={0}
                                    dy={4}
                                    stdDeviation={4}
                                    floodColor="#000000"
                                    floodOpacity={0.03}
                                    result="checkpointOffsetShadow"
                                />
                                <feDropShadow
                                    in="SourceGraphic"
                                    dx={0}
                                    dy={0}
                                    stdDeviation={2}
                                    floodColor="#000000"
                                    floodOpacity={0.06}
                                    result="checkpointAmbientShadow"
                                />
                                <feMerge>
                                    <feMergeNode in="checkpointOffsetShadow" />
                                    <feMergeNode in="checkpointAmbientShadow" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </>
                    ) : null}
                </defs>
                <Group top={topBufferSize}>
                    {/* Diverging area fill between the line and the 0 baseline (red above, blue below).
                        The same area path is painted twice, each clipped to its side of the 0 line.
                        Wrapped in the reveal clip so it draws in with the line. Drawn first so gridlines,
                        the 0-axis, and the line all sit on top. */}
                    <g clipPath={`url(#${revealClipId})`}>
                        <Area<KpowHistoryPoint>
                            data={linePoints}
                            x={xAccessor}
                            y0={(): number => yScale(0) ?? 0}
                            y1={yAccessor}
                            curve={curveLinear}
                        >
                            {({ path }): JSX.Element => {
                                const d = path(linePoints) || '';
                                return (
                                    <>
                                        <path
                                            d={d}
                                            fill={`url(#${fillGradientId}-r)`}
                                            clipPath={`url(#${fillGradientId}-above)`}
                                        />
                                        <path
                                            d={d}
                                            fill={`url(#${fillGradientId}-b)`}
                                            clipPath={`url(#${fillGradientId}-below)`}
                                        />
                                    </>
                                );
                            }}
                        </Area>
                    </g>
                    {/* Faint gridline only at the mid ticks (+/-half); the top/bottom edges are the axis
                        frame, and 0 gets its own line below. */}
                    <GridRows
                        scale={yScale}
                        width={primaryContentWidth}
                        height={primaryContentHeight}
                        stroke={Palette.Fill.x30}
                        tickValues={
                            isMidterms
                                ? ticks.filter((tick) => tick !== 0)
                                : ticks.filter((t) => t !== 0 && t !== domainMax && t !== domainMin)
                        }
                        strokeDasharray="1,4"
                    />
                    {/* Give the neutral axis more contrast than the surrounding grid. Midterms keeps
                        the dotted treatment; KPOW retains its solid muted-purple midpoint. */}
                    {isMidterms ? (
                        <line
                            data-chart-gridline="tie"
                            x1={0}
                            x2={primaryContentWidth}
                            y1={yScale(0)}
                            y2={yScale(0)}
                            stroke={Palette.Text.x20}
                            strokeWidth={1}
                            strokeDasharray="1,4"
                        />
                    ) : (
                        <line
                            x1={0}
                            x2={primaryContentWidth}
                            y1={yScale(0)}
                            y2={yScale(0)}
                            stroke="#944798"
                            strokeWidth={1}
                        />
                    )}
                    {/* Full-width bottom frame bar closing off the plot, matching the divider above the
                        chart. Sits just below the plot (clearing the bottom y-label); the month labels
                        sit below it. Spans the whole width (not just the plot) to line up with the top. */}
                    {!isMidterms ? (
                        <line
                            x1={0}
                            x2={width}
                            y1={primaryContentHeight + AXIS_BAR_OFFSET}
                            y2={primaryContentHeight + AXIS_BAR_OFFSET}
                            stroke={Palette.Text.x10}
                            strokeWidth={1}
                        />
                    ) : null}
                    {/* Faint guide line dropped from each annotation, spanning the full framed chart:
                        from the bottom frame bar up to the tab-row divider above the SVG (reached via
                        overflow:visible). The hovered one darkens to a solid line. Behind line and dots. */}
                    {!isMidterms
                        ? annotationMarks.map((mark, i) => (
                              <line
                                  key={`guide-${mark.annotation.ts}`}
                                  x1={mark.cx}
                                  x2={mark.cx}
                                  y1={-(topBufferSize + TAB_DIVIDER_GAP)}
                                  y2={primaryContentHeight + AXIS_BAR_OFFSET}
                                  stroke={activeAnnotation === i ? Palette.Text.x20 : Palette.Fill.x30}
                                  strokeWidth={1}
                                  strokeDasharray={activeAnnotation === i ? undefined : '2,3'}
                                  pointerEvents="none"
                              />
                          ))
                        : null}
                    <Axis
                        xScale={xScale}
                        yScale={yScale}
                        gridWidth={primaryContentWidth}
                        height={primaryContentHeight}
                        xTicks={xTicks}
                        xTickFormat={xAxisTickFormat}
                        hideXAxis
                        hideYAxis
                    />
                    {/* Custom x-axis: middle-anchored labels at every tick so "May 1" sits
                        directly above its xScale position. The DS2 helper end-anchors the
                        last tick to avoid edge overflow, which on the KPOW chart (whose
                        last data point is well past May 1) makes "May" appear at ~Apr 26.
                        Midterms keeps its axis baseline just below the bottom (+D max) gridline,
                        adding 4px of breathing room to AxisBottom's built-in label placement. */}
                    <AxisBottom
                        top={primaryContentHeight + (isMidterms ? MIDTERMS_AXIS_LABEL_OFFSET : AXIS_LABEL_OFFSET)}
                        scale={xScale}
                        tickValues={xTicks}
                        hideAxisLine
                        tickLength={4}
                        tickLineProps={{ strokeWidth: 0 }}
                        tickFormat={(date): string =>
                            date instanceof Date ? date.toLocaleString('en-US', xAxisTickFormat) : `${date}`
                        }
                        tickLabelProps={(_, index) => ({
                            fill: Palette.Text.x20,
                            fontSize: 12,
                            fontFamily: 'var(--font-kalshi-sans)',
                            // Center every label on its tick; only fall back to start/end anchoring
                            // for a label that would otherwise spill off the left/right edge, so the
                            // first month sits centered like the rest instead of shoved right.
                            textAnchor: edgeSafeAnchor(xScale(xTicks[index]) ?? 0, primaryContentWidth),
                            pointerEvents: 'none',
                        })}
                    />
                    {/* Vertical strip on the y-axis. Reuses the line gradient (solid red above 0,
                        hard flip to solid blue below), matching the line coloring. Sits AFTER the
                        y-tick labels at the far-right edge. */}
                    {!isMidterms ? (
                        <rect
                            x={primaryContentWidth + Y_TICK_LENGTH + Y_AXIS_LABEL_WIDTH}
                            y={yScale(domainMax) - Y_STRIP_OVERHANG_TOP}
                            width={Y_AXIS_STRIP_WIDTH}
                            height={
                                yScale(domainMin) - yScale(domainMax) + Y_STRIP_OVERHANG_TOP + Y_STRIP_OVERHANG_BOTTOM
                            }
                            fill={`url(#${gradientId})`}
                            rx={1.5}
                        />
                    ) : null}
                    <AxisRight
                        left={primaryContentWidth}
                        scale={yScale}
                        hideAxisLine
                        tickValues={ticks}
                        tickLength={yTickLength}
                        tickLineProps={{ strokeWidth: 0 }}
                        tickFormat={(value): string =>
                            isMidterms
                                ? MIDTERMS_Y_TICK_FORMAT(Number(value), compactMidtermsAxis)
                                : Y_TICK_FORMAT(Number(value))
                        }
                        tickComponent={isMidterms ? MidtermsYAxisTick : undefined}
                        tickLabelProps={(value) => ({
                            fill:
                                isMidterms && Math.abs(Number(value)) < 0.05
                                    ? Palette.Text.x10
                                    : yTickColor(Number(value)),
                            stroke: Palette.Surface.x10,
                            strokeWidth: 2,
                            paintOrder: 'stroke',
                            fontSize: 12,
                            fontWeight: 400,
                            fontFamily: 'var(--font-kalshi-sans)',
                            textAnchor: 'start',
                            verticalAnchor: 'middle',
                            pointerEvents: 'none',
                            style: {
                                fontVariantNumeric: 'lining-nums tabular-nums',
                                fontFeatureSettings: "'case' on",
                            },
                        })}
                    />
                    {/* Line, wrapped in the reveal clip so it draws in on load. While scrubbing, dim the
                        line to the RIGHT of the cursor (the "future") via clip rects: full opacity
                        before activeX, 0.25 after. */}
                    <g clipPath={`url(#${revealClipId})`}>
                        {scrubPoint ? (
                            <>
                                <defs>
                                    <clipPath id={`${gradientId}-scrub-before`}>
                                        <rect x={0} y={0} width={activeX} height={primaryContentHeight} />
                                    </clipPath>
                                    <clipPath id={`${gradientId}-scrub-after`}>
                                        <rect
                                            x={activeX}
                                            y={0}
                                            width={Math.max(0, primaryContentWidth - activeX)}
                                            height={primaryContentHeight}
                                        />
                                    </clipPath>
                                </defs>
                                <g clipPath={`url(#${gradientId}-scrub-before)`}>{line}</g>
                                <g clipPath={`url(#${gradientId}-scrub-after)`} opacity={0.25}>
                                    {line}
                                </g>
                            </>
                        ) : (
                            line
                        )}
                    </g>
                    {lastPoint ? (
                        <g pointerEvents="none" clipPath={isMidterms ? undefined : `url(#${revealClipId})`}>
                            {isMidterms ? (
                                <>
                                    <g className="midterms-trailing-point-glow-burst">
                                        <circle
                                            data-chart-glow="trailing"
                                            className="midterms-trailing-point-glow"
                                            cx={lastX}
                                            cy={lastY}
                                            r={4}
                                            fill={lastColor}
                                            stroke="none"
                                        />
                                    </g>
                                    <circle data-chart-marker="trailing" cx={lastX} cy={lastY} r={4} fill={lastColor} />
                                </>
                            ) : (
                                <>
                                    {/* Pulsing halo signals live data on KPOW. */}
                                    <circle cx={lastX} cy={lastY} r={6} fill={lastColor} opacity={0.25}>
                                        <animate attributeName="r" values="5;14;5" dur="2s" repeatCount="indefinite" />
                                        <animate
                                            attributeName="opacity"
                                            values="0.35;0;0.35"
                                            dur="2s"
                                            repeatCount="indefinite"
                                        />
                                    </circle>
                                    <circle cx={lastX} cy={lastY} r={4} fill={lastColor} />
                                </>
                            )}
                        </g>
                    ) : null}
                    {/* Scrub marker: the hovered reading's date capping a dotted guide, plus a ringed
                        dot on the reading itself. The hovered VALUE is not repeated here — both
                        presentations reflect it in their headline. `hideLine` drops DateLine's 2px
                        solid bar so the 1px dotted variant can start at the label clearance instead.
                        Midterms pins day precision (its axis ticks are month-only, and the headline
                        carries the year); KPOW follows the range-derived tick format. */}
                    {scrubPoint ? (
                        <g pointerEvents="none">
                            <DateLine
                                xDefault={activeX}
                                xMin={HORIZONTAL_PADDING}
                                xMax={primaryContentWidth}
                                barTopY={0}
                                barBottomY={primaryContentHeight}
                                date={pointDate(scrubPoint.end_period_ts)}
                                formatOptions={isMidterms ? MIDTERMS_SCRUB_DATE_FORMAT : xTickFormat}
                                hideLine
                            />
                            <line
                                data-chart-scrub="guide"
                                x1={activeX}
                                y1={DATE_LINE_LABEL_CLEARANCE}
                                x2={activeX}
                                y2={isMidterms ? primaryContentHeight : primaryContentHeight + AXIS_BAR_OFFSET}
                                stroke={isMidterms ? Palette.Text.x20 : Palette.Fill.x20}
                                strokeWidth={1}
                                strokeDasharray="2,3"
                            />
                            <circle
                                data-chart-scrub="marker"
                                cx={activeX}
                                cy={activeY}
                                r={4}
                                fill={Palette.Surface.x10}
                                stroke={
                                    isMidterms
                                        ? midtermsPartyColor(scrubPoint.signed_value)
                                        : scrubPoint.signed_value >= 0
                                          ? KPOW_RED
                                          : KPOW_BLUE
                                }
                                strokeWidth={2}
                            />
                        </g>
                    ) : null}
                    {/* Midterms checkpoints use the Figma 12px plot point with a 1px border. KPOW keeps
                        its existing frosted/active marker treatment. */}
                    <g clipPath={`url(#${revealClipId})`}>
                        {annotationMarks.map((mark, i) => {
                            const active = activeAnnotation === i;
                            const ahead = !isMidterms && scrubPoint !== null && !active && mark.cx > activeX;
                            return (
                                <g
                                    key={mark.annotation.ts}
                                    opacity={ahead ? 0.3 : 1}
                                    pointerEvents={isMidterms ? 'all' : 'none'}
                                    tabIndex={isMidterms ? 0 : undefined}
                                    role={isMidterms ? 'button' : undefined}
                                    aria-label={
                                        isMidterms
                                            ? `${formatLongDate(mark.annotation.ts)}: ${mark.annotation.label}`
                                            : undefined
                                    }
                                    onFocus={isMidterms ? () => setActiveAnnotation(i) : undefined}
                                    onBlur={isMidterms ? () => setActiveAnnotation(null) : undefined}
                                    onMouseEnter={isMidterms ? () => setActiveAnnotation(i) : undefined}
                                    onKeyDown={
                                        isMidterms
                                            ? (event) => {
                                                  if (event.key === 'Escape') {
                                                      setActiveAnnotation(null);
                                                      event.currentTarget.blur();
                                                  } else if (event.key === 'Enter' || event.key === ' ') {
                                                      event.preventDefault();
                                                      setActiveAnnotation(i);
                                                  }
                                              }
                                            : undefined
                                    }
                                >
                                    {isMidterms ? <circle cx={mark.cx} cy={mark.cy} r={16} fill="transparent" /> : null}
                                    {isMidterms ? (
                                        <circle
                                            data-chart-marker="checkpoint"
                                            cx={mark.cx}
                                            cy={mark.cy}
                                            r={5.5}
                                            fill={Palette.Text.x10}
                                            stroke={Palette.Surface.x10}
                                            strokeWidth={1}
                                            filter={`url(#${checkpointShadowId})`}
                                        />
                                    ) : active ? (
                                        <circle
                                            cx={mark.cx}
                                            cy={mark.cy}
                                            r={6.5}
                                            fill={Palette.Text.x10}
                                            stroke={Palette.Surface.x10}
                                            strokeWidth={1}
                                        />
                                    ) : (
                                        <>
                                            <circle
                                                cx={mark.cx}
                                                cy={mark.cy}
                                                r={9.5}
                                                fill="rgba(255,255,255,0.48)"
                                                stroke="rgba(0,0,0,0.3)"
                                                strokeWidth={1}
                                            />
                                            <circle
                                                cx={mark.cx}
                                                cy={mark.cy}
                                                r={3.5}
                                                fill={Palette.Text.x10}
                                                stroke={Palette.Surface.x10}
                                                strokeWidth={1}
                                            />
                                        </>
                                    )}
                                </g>
                            );
                        })}
                    </g>
                </Group>
            </svg>
            {activeMark ? (
                <div
                    role={isMidterms ? 'tooltip' : undefined}
                    className={`pointer-events-none absolute z-10 -translate-x-1/2 ${
                        !isMidterms || checkpointTooltipAbove ? '-translate-y-full' : ''
                    }`}
                    style={{
                        left: isMidterms ? checkpointTooltipX : activeMark.cx,
                        top: isMidterms
                            ? checkpointTooltipY +
                              (checkpointTooltipAbove ? -CHECKPOINT_TOOLTIP_GAP : CHECKPOINT_TOOLTIP_GAP)
                            : topBufferSize + activeMark.cy - 10,
                    }}
                >
                    <div
                        className={
                            isMidterms
                                ? 'flex flex-col gap-0.5 break-words rounded-x40 border border-solid border-[rgba(0,0,0,0.06)] bg-core-white p-2 shadow-x20 dark:shadow-[0_0_12px_rgba(255,255,255,0.14),0_12px_24px_rgba(0,0,0,0.4)]'
                                : 'flex w-[216px] flex-col gap-0.5 rounded-[13px] border border-solid border-[rgba(0,0,0,0.2)] bg-[rgba(255,255,255,0.82)] px-[14px] py-[10px] backdrop-blur-[2px]'
                        }
                        style={isMidterms ? { width: checkpointTooltipWidth } : undefined}
                    >
                        <Emphasis.x20
                            className={isMidterms ? '!leading-[18px] text-[rgba(0,0,0,0.9)]' : 'text-text-x10'}
                        >
                            {formatLongDate(activeMark.annotation.ts)}
                        </Emphasis.x20>
                        <Body.x20 className={isMidterms ? '!leading-[18px] text-[rgba(0,0,0,0.9)]' : 'text-text-x10'}>
                            {activeMark.annotation.label}
                        </Body.x20>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default IndexGraphContent;
